import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { Logo } from "brand";
import { useActiveOrg, PinLock } from "common-fe";
import { useTranslation } from "i18n-fe";
import { useShows } from "../hooks/useShows";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { ringsFor } from "../utils/rings";

// #641 — org -> show -> ring setup, mirrors scribe-fe's InitialSetup shape:
// sticky (persisted by AnnouncerProvider), pickers collapsed once a session
// exists, reopened only by the PIN lock (testId="announcer-lock").
export default function InitialSetup() {
  const navigate = useNavigate();
  const { t } = useTranslation("announcer");
  const {
    selectedShow,
    selectedRing,
    online,
    isSessionReady,
    pickerUnlocked,
    unlockPicker,
    startSession,
  } = useAnnouncerContext();
  const { orgs, activeOrgId, setActiveOrgId } = useActiveOrg();
  const { data: shows, loading } = useShows(online);

  const [showId, setShowId] = useState(selectedShow?.id ?? "");
  const [ring, setRing] = useState(selectedRing ?? "");

  const { data: classList, loading: loadingClasses } = useClassesForShow(showId || null, online);
  const rings = useMemo(() => ringsFor(classList), [classList]);

  // A ring picked before its show's class list loaded (or a ring that no
  // longer exists for this show) is invalid — clear it so the Select doesn't
  // hold a stale value.
  useEffect(() => {
    if (ring && rings.length && !rings.includes(ring)) setRing("");
  }, [rings, ring]);

  const handleOrgChange = (e) => {
    setActiveOrgId(e.target.value);
    // Shows (and their rings) are org-scoped — a stale selection is invalid.
    setShowId("");
    setRing("");
  };

  const handleShowChange = (e) => {
    setShowId(e.target.value);
    setRing("");
  };

  // #628-style — the org/show/ring selectors stay behind the PIN lock. A
  // first-time visitor (no session yet) has nothing to protect, so they're
  // open; after that, only a PIN unlock reopens them (AC#2).
  const selectorsUnlocked = !isSessionReady || pickerUnlocked;

  const canStart = Boolean(showId) && Boolean(ring);

  const handleStart = () => {
    const show = (shows ?? []).find((s) => s.id === showId);
    if (!show || !ring) return;
    startSession(show, ring);
    navigate("/oog", { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Card elevation={4} sx={{ width: "100%", maxWidth: 560, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Logo style={{ width: 160, height: "auto" }} />
          </Box>
          {/* #641 — lock self-positions fixed bottom-right. /setup is outside
              any app shell, so it mounts its own, same as scribe's #145/#628. */}
          <PinLock onUnlock={unlockPicker} testId="announcer-lock" />

          <Typography variant="h5" fontWeight={700} align="center" sx={{ mb: 3 }}>
            {t("app.title")}
          </Typography>

          {isSessionReady && !selectorsUnlocked && (
            <Stack spacing={1} alignItems="center" sx={{ mb: 2 }} data-testid="current-session">
              <Typography variant="body2" color="text.secondary">
                {t("setup.currentSession")}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {selectedShow?.name}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t("setup.ringLabel")}: {selectedRing}
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 1 }}
                onClick={() => navigate("/oog")}
                data-testid="setup-continue"
              >
                {t("setup.continue")}
              </Button>
            </Stack>
          )}

          {selectorsUnlocked ? (
            <Stack spacing={3} sx={{ mt: isSessionReady ? 3 : 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t("setup.title")}
              </Typography>
              {orgs.length > 1 && (
                <FormControl fullWidth>
                  <InputLabel>{t("setup.orgLabel")}</InputLabel>
                  <Select
                    value={activeOrgId ?? ""}
                    label={t("setup.orgLabel")}
                    onChange={handleOrgChange}
                    disabled={loading}
                    data-testid="setup-org-select"
                  >
                    {orgs.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth>
                <InputLabel>{t("setup.showLabel")}</InputLabel>
                <Select
                  value={showId}
                  label={t("setup.showLabel")}
                  onChange={handleShowChange}
                  disabled={loading}
                  data-testid="setup-show-select"
                >
                  {(shows ?? []).map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                      {s.dateRange ? ` — ${s.dateRange}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth disabled={!showId}>
                <InputLabel>{t("setup.ringLabel")}</InputLabel>
                <Select
                  value={ring}
                  label={t("setup.ringLabel")}
                  onChange={(e) => setRing(e.target.value)}
                  disabled={!showId || loadingClasses}
                  data-testid="setup-ring-select"
                >
                  {rings.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={!canStart || loading}
                onClick={handleStart}
                startIcon={
                  loading || loadingClasses ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{ py: 1.5, fontWeight: 600 }}
                data-testid="setup-start"
              >
                {t("setup.start")}
              </Button>

              {/* #734 — kiosk lockdown: Account settings moved behind the
                  PIN lock. Only reachable here, inside the unlocked admin
                  screen, never from a free-standing menu. */}
              <Divider />
              <Button
                variant="text"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  window.location.href =
                    import.meta.env.VITE_ACCOUNT_FE_BASE_URL + "/settings";
                }}
              >
                Account settings
              </Button>
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}
